<script>
  import { onMount } from 'svelte';
  import { getApiUrl } from '../api';
  import { authFetch } from '../authService';
  import { t } from '../i18nStore';
  import { notificationStore } from '../notificationStore';

  let blurLaplacianThreshold = 90.0;
  let blankStdDevThreshold = 8.0;
  let spamBanThreshold = 3;
  let banDurationHours = 24;
  let defaultLocationRadiusKm = 2.0;
  let geofenceLimitMeters = 200;
  let duplicateHammingThreshold = 3;
  let trustedTemplateProviders = '';

  let isLoading = true;
  let isSaving = false;
  let error = '';

  onMount(fetchSettings);

  async function fetchSettings() {
    isLoading = true;
    error = '';
    try {
      const res = await authFetch(getApiUrl('/admin/system-settings'));
      if (!res.ok) throw new Error('Failed to load system settings');
      const data = await res.json();
      blurLaplacianThreshold = data.blurLaplacianThreshold || 90.0;
      blankStdDevThreshold = data.blankStdDevThreshold || 8.0;
      spamBanThreshold = data.spamBanThreshold || 3;
      banDurationHours = data.banDurationHours || 24;
      defaultLocationRadiusKm = data.defaultLocationRadiusMeters
        ? (data.defaultLocationRadiusMeters / 1000.0)
        : 2.0;
      geofenceLimitMeters = data.geofenceLimitMeters !== undefined ? data.geofenceLimitMeters : 200;
      duplicateHammingThreshold = data.duplicateHammingThreshold !== undefined ? data.duplicateHammingThreshold : 3;
      trustedTemplateProviders = Array.isArray(data.trustedTemplateProviders)
        ? data.trustedTemplateProviders.join(', ')
        : 'PayMe, Alipay, AlipayHK, WeChat Pay, WeChat, Octopus, BoC Pay, HSBC Reward+';
    } catch (err) {
      error = err.message;
    } finally {
      isLoading = false;
    }
  }

  async function handleSave() {
    isSaving = true;
    error = '';
    try {
      const providersArray = trustedTemplateProviders
        .split(',')
        .map(s => s.trim())
        .filter(Boolean);

      const parsedHamming = parseInt(duplicateHammingThreshold, 10);
      const payload = {
        blurLaplacianThreshold: parseFloat(blurLaplacianThreshold),
        blankStdDevThreshold: parseFloat(blankStdDevThreshold),
        spamBanThreshold: parseInt(spamBanThreshold, 10),
        banDurationHours: parseInt(banDurationHours, 10),
        defaultLocationRadiusMeters: parseFloat(defaultLocationRadiusKm) * 1000.0,
        geofenceLimitMeters: parseFloat(geofenceLimitMeters) || 200,
        duplicateHammingThreshold: isNaN(parsedHamming) ? 3 : parsedHamming,
        trustedTemplateProviders: providersArray,
      };

      const res = await authFetch(getApiUrl('/admin/system-settings'), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.error || $t('admin.system_settings.save_error'));
      }

      notificationStore.set({
        message: $t('admin.system_settings.save_success'),
        type: 'info',
      });
    } catch (err) {
      error = err.message;
      notificationStore.set({
        message: err.message,
        type: 'admin',
      });
    } finally {
      isSaving = false;
    }
  }
</script>

<div class="admin-container">
  <div class="header-section">
    <h2>{$t('admin.system_settings.title')}</h2>
    <p class="subtitle">{$t('admin.system_settings.subtitle')}</p>
  </div>

  {#if isLoading}
    <div class="loading-box">
      <p>{$t('common.loading')}</p>
    </div>
  {:else}
    {#if error}
      <div class="error-banner">{error}</div>
    {/if}

    <form class="settings-form" on:submit|preventDefault={handleSave}>
      <!-- 1. Blur Threshold -->
      <div class="form-group">
        <label for="blur-input">{$t('admin.system_settings.blur_threshold')}</label>
        <p class="field-hint">{$t('admin.system_settings.blur_hint')}</p>
        <div class="input-slider-row">
          <input
            id="blur-input"
            type="number"
            min="10"
            max="300"
            step="1"
            bind:value={blurLaplacianThreshold}
            class="num-input"
          />
          <input
            type="range"
            min="20"
            max="200"
            step="1"
            bind:value={blurLaplacianThreshold}
            class="slider"
          />
        </div>
      </div>

      <!-- 2. Blank Image Threshold -->
      <div class="form-group">
        <label for="blank-input">{$t('admin.system_settings.blank_threshold')}</label>
        <p class="field-hint">{$t('admin.system_settings.blank_hint')}</p>
        <div class="input-slider-row">
          <input
            id="blank-input"
            type="number"
            min="1"
            max="30"
            step="0.5"
            bind:value={blankStdDevThreshold}
            class="num-input"
          />
          <input
            type="range"
            min="2"
            max="20"
            step="0.5"
            bind:value={blankStdDevThreshold}
            class="slider"
          />
        </div>
      </div>

      <!-- 3. Spam Strike Limit -->
      <div class="form-group">
        <label for="spam-input">{$t('admin.system_settings.spam_ban_threshold')}</label>
        <p class="field-hint">{$t('admin.system_settings.spam_hint')}</p>
        <input
          id="spam-input"
          type="number"
          min="1"
          max="10"
          step="1"
          bind:value={spamBanThreshold}
          class="num-input-small"
        />
      </div>

      <!-- 4. Ban Duration (Hours) -->
      <div class="form-group">
        <label for="duration-input">{$t('admin.system_settings.ban_duration')}</label>
        <p class="field-hint">{$t('admin.system_settings.ban_duration_hint')}</p>
        <input
          id="duration-input"
          type="number"
          min="1"
          max="168"
          step="1"
          bind:value={banDurationHours}
          class="num-input-small"
        />
      </div>

      <!-- 5. Default Nearby & Live Location Search Radius (km) -->
      <div class="form-group">
        <label for="radius-input">{$t('admin.system_settings.radius_title')}</label>
        <p class="field-hint">{$t('admin.system_settings.radius_hint')}</p>
        <div class="input-slider-row">
          <div class="input-with-unit">
            <input
              id="radius-input"
              type="number"
              min="0.1"
              max="20.0"
              step="0.1"
              bind:value={defaultLocationRadiusKm}
              class="num-input"
            />
            <span class="unit-label">km</span>
          </div>
          <input
            type="range"
            min="0.5"
            max="10.0"
            step="0.1"
            bind:value={defaultLocationRadiusKm}
            class="slider"
          />
          <span class="badge-meters">{(parseFloat(defaultLocationRadiusKm || 0) * 1000).toFixed(0)} m</span>
        </div>
      </div>

      <!-- 6. Geofence Distance Limit (m) -->
      <div class="form-group">
        <label for="geofence-input">{$t('admin.system_settings.geofence_limit')}</label>
        <p class="field-hint">{$t('admin.system_settings.geofence_hint')}</p>
        <div class="input-slider-row">
          <div class="input-with-unit">
            <input
              id="geofence-input"
              type="number"
              min="10"
              max="2000"
              step="10"
              bind:value={geofenceLimitMeters}
              class="num-input"
            />
            <span class="unit-label">m</span>
          </div>
          <input
            type="range"
            min="50"
            max="1000"
            step="10"
            bind:value={geofenceLimitMeters}
            class="slider"
          />
          <span class="badge-meters">{geofenceLimitMeters} m</span>
        </div>
      </div>

      <!-- 7. Duplicate Image Hamming Distance Threshold -->
      <div class="form-group">
        <label for="hamming-input">{$t('admin.system_settings.duplicate_hamming_threshold')}</label>
        <p class="field-hint">{$t('admin.system_settings.duplicate_hamming_hint')}</p>
        <div class="input-slider-row">
          <input
            id="hamming-input"
            type="number"
            min="0"
            max="16"
            step="1"
            bind:value={duplicateHammingThreshold}
            class="num-input"
          />
          <input
            type="range"
            min="0"
            max="12"
            step="1"
            bind:value={duplicateHammingThreshold}
            class="slider"
          />
        </div>
      </div>

      <!-- 8. Trusted Template Providers -->
      <div class="form-group">
        <label for="providers-input">{$t('admin.system_settings.trusted_template_providers')}</label>
        <p class="field-hint">{$t('admin.system_settings.trusted_template_providers_hint')}</p>
        <input
          id="providers-input"
          type="text"
          bind:value={trustedTemplateProviders}
          class="text-input"
          placeholder="PayMe, Alipay, AlipayHK, WeChat Pay, WeChat, Octopus, BoC Pay, HSBC Reward+"
        />
      </div>

      <div class="actions-row">
        <button type="submit" class="btn-save" disabled={isSaving}>
          {isSaving ? $t('admin.system_settings.saving') : $t('admin.system_settings.save')}
        </button>
      </div>
    </form>
  {/if}
</div>

<style>
  .admin-container {
    max-width: 750px;
    margin: 2rem auto;
    padding: 2rem;
    background: #ffffff;
    border-radius: 12px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
  }

  .header-section {
    margin-bottom: 2rem;
    border-bottom: 1px solid #f0f0f0;
    padding-bottom: 1rem;
  }

  h2 {
    font-size: 1.6rem;
    color: #1a1a1a;
    margin-bottom: 0.4rem;
  }

  .subtitle {
    color: #666;
    font-size: 0.95rem;
    margin: 0;
  }

  .loading-box {
    text-align: center;
    padding: 3rem 0;
    color: #888;
  }

  .error-banner {
    background: #ffebe9;
    color: #cf222e;
    border: 1px solid #ffc1c0;
    padding: 0.8rem 1rem;
    border-radius: 6px;
    margin-bottom: 1.5rem;
    font-size: 0.95rem;
  }

  .settings-form {
    display: flex;
    flex-direction: column;
    gap: 1.8rem;
  }

  .form-group {
    display: flex;
    flex-direction: column;
  }

  label {
    font-weight: 600;
    font-size: 1.05rem;
    color: #222;
    margin-bottom: 0.2rem;
  }

  .field-hint {
    font-size: 0.88rem;
    color: #777;
    margin-bottom: 0.6rem;
  }

  .input-slider-row {
    display: flex;
    align-items: center;
    gap: 1.2rem;
  }

  .num-input {
    width: 100px;
    padding: 0.5rem 0.8rem;
    border: 1px solid #ccc;
    border-radius: 6px;
    font-size: 1rem;
  }

  .num-input-small {
    width: 120px;
    padding: 0.5rem 0.8rem;
    border: 1px solid #ccc;
    border-radius: 6px;
    font-size: 1rem;
  }

  .text-input {
    width: 100%;
    box-sizing: border-box;
    padding: 0.6rem 0.8rem;
    border: 1px solid #ccc;
    border-radius: 6px;
    font-size: 0.95rem;
  }

  .slider {
    flex: 1;
    cursor: pointer;
  }

  .input-with-unit {
    display: flex;
    align-items: center;
    gap: 0.4rem;
  }

  .unit-label {
    font-size: 0.95rem;
    font-weight: 600;
    color: #555;
  }

  .badge-meters {
    background: #eef4fc;
    color: #0070ba;
    font-size: 0.88rem;
    font-weight: 600;
    padding: 0.4rem 0.7rem;
    border-radius: 6px;
    min-width: 75px;
    text-align: center;
    border: 1px solid #d0e2f9;
  }

  .actions-row {
    margin-top: 1rem;
    border-top: 1px solid #f0f0f0;
    padding-top: 1.5rem;
    display: flex;
    justify-content: flex-end;
  }

  .btn-save {
    background: #0070ba;
    color: #fff;
    border: none;
    padding: 0.75rem 1.8rem;
    border-radius: 8px;
    font-weight: 600;
    font-size: 1rem;
    cursor: pointer;
    transition: background 0.2s ease;
  }

  .btn-save:hover:not(:disabled) {
    background: #005ea6;
  }

  .btn-save:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
</style>
